<template>
  <div class="usr">
    <!-- Zona 1A — Equipo del parque (usuarios internos) -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Equipo del parque</span>
        <span class="usr-group__count">({{ users.length }})</span>
      </div>

      <table class="usr-table" v-if="users.length">
        <tbody>
          <tr v-for="u in users" :key="u.id" class="usr-table__row">
            <td class="usr-table__col usr-table__col--avatar">
              <div class="usr-avatar" :style="{ backgroundColor: u.avatarColor }">{{ initials(u) }}</div>
            </td>
            <td class="usr-table__col usr-table__col--name">
              <div class="usr-table__name-line">
                {{ u.name || u.email }}
                <span v-if="u.id === currentUserId" class="usr-tag-you">Tú</span>
              </div>
              <div class="usr-table__email-line">{{ u.email }}</div>
            </td>
            <td class="usr-table__col usr-table__col--role">
              <span class="usr-role-pill" :class="`usr-role-pill--${u.role.toLowerCase()}`">
                {{ roleLabel(u) }}
              </span>
              <div v-if="u.role === 'TECHNICIAN'" class="usr-tech-link" :class="{ 'usr-tech-link--missing': !u.technician }">
                <span class="mdi" :class="u.technician ? 'mdi-link-variant' : 'mdi-link-variant-off'"></span>
                {{ u.technician ? u.technician.name : 'Sin técnico vinculado' }}
              </div>
            </td>
            <td class="usr-table__col usr-table__col--access">
              <span class="usr-access-text" :class="{ 'usr-access--pending': u.status === 'PENDING' }">
                {{ formatRelativeAccess(u) }}
              </span>
            </td>
            <td class="usr-table__col usr-table__col--actions">
              <button class="usr-actions-btn" @click.stop="toggleMenu(u.id, $event)">
                <span class="mdi mdi-dots-vertical"></span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="usr-empty">Aún no hay usuarios con acceso.</div>
      <div v-if="linkError" class="usr-link-error">{{ linkError }}</div>

      <Teleport to="body">
        <div v-if="openMenuUserId" class="usr-menu" :style="menuPosition" @click.stop>
          <template v-if="techMenuUserId === openMenuUserId">
            <div class="usr-menu__role-picker">
              <button
                v-for="t in technicians"
                :key="t.id"
                class="usr-menu__role-opt"
                :disabled="isTechnicianTaken(t.id, openMenuUserId)"
                @click="onLinkTechnician(openMenuUserId, t.id)"
              >
                {{ t.name }}<span v-if="isTechnicianTaken(t.id, openMenuUserId)" class="usr-menu__taken"> · ya vinculado</span>
              </button>
              <div v-if="!technicians.length" class="usr-menu__empty">No hay técnicos en el catálogo.</div>
              <button v-if="activeMenuUser?.technician" class="usr-menu__role-opt usr-menu__role-opt--danger" @click="onLinkTechnician(openMenuUserId, null)">
                Quitar vínculo
              </button>
            </div>
          </template>
          <template v-else>
          <button
            v-if="activeMenuUser?.role === 'TECHNICIAN'"
            class="usr-menu__item"
            @click="techMenuUserId = openMenuUserId; roleMenuUserId = null"
          >
            {{ activeMenuUser.technician ? 'Cambiar técnico vinculado' : 'Vincular técnico' }}
          </button>
          <button
            v-if="roleMenuUserId !== openMenuUserId"
            class="usr-menu__item"
            @click="roleMenuUserId = openMenuUserId"
          >
            Cambiar rol
          </button>
          <div v-else class="usr-menu__role-picker">
            <button
              v-for="opt in roleOptions"
              :key="opt.value"
              class="usr-menu__role-opt"
              @click="onChangeRole(openMenuUserId, opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <button
            v-if="activeMenuUser?.status === 'PENDING'"
            class="usr-menu__item"
            @click="onResendInvite(openMenuUserId)"
          >
            Reenviar invitación
          </button>
          <button
            v-if="activeMenuUser?.status !== 'SUSPENDED'"
            class="usr-menu__item"
            @click="onSuspend(openMenuUserId)"
          >
            Suspender acceso
          </button>
          <button v-else class="usr-menu__item" @click="onReactivate(openMenuUserId)">
            Reactivar acceso
          </button>
          <button
            v-if="openMenuUserId !== currentUserId"
            class="usr-menu__item usr-menu__item--danger"
            @click="confirmDeleteUserId = openMenuUserId"
          >
            Eliminar usuario
          </button>
          </template>
        </div>
      </Teleport>
    </section>

    <hr class="usr-divider" />

    <!-- Zona 1B — Accesos externos (técnicos e inquilinos) -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Accesos externos</span>
        <span class="usr-group__count">({{ externalAccesses.length }})</span>
      </div>
      <div class="usr-group__hint">
        Los técnicos e inquilinos no se invitan desde aquí: su acceso se activa desde su registro de origen
        (Técnicos o Contratos). Aquí solo se administra quién ya tiene acceso activo.
      </div>

      <table class="usr-table" v-if="externalAccesses.length">
        <tbody>
          <tr v-for="row in externalAccesses" :key="row.id" class="usr-table__row">
            <td class="usr-table__col usr-table__col--name">
              <div class="usr-table__name-line">{{ row.name }}</div>
              <div class="usr-table__email-line">{{ row.originLabel }}</div>
            </td>
            <td class="usr-table__col usr-table__col--role">
              <span
                class="usr-role-pill"
                :class="row.sourceType === 'TECHNICIAN' ? 'usr-role-pill--technician' : 'usr-role-pill--tenant'"
              >
                {{ row.typeLabel }}
              </span>
            </td>
            <td class="usr-table__col usr-table__col--access">Sin acceso aún</td>
            <td class="usr-table__col usr-table__col--actions">
              <label class="usr-toggle" title="Desactivar acceso">
                <input type="checkbox" checked :disabled="togglingId === row.id" @change="onToggleExternalAccess(row)" />
                <span class="usr-toggle__slider"></span>
              </label>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="usr-empty">Aún no hay accesos externos activos.</div>
    </section>

    <!-- Zona 2 — Invitar usuario -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Invitar usuario</span>
      </div>

      <div class="usr-invite-note">
        Este formulario es para invitar al equipo administrativo del parque. Para dar acceso a técnicos o
        inquilinos usa los controles de las secciones anteriores.
      </div>

      <div class="usr-invite">
        <div class="usr-invite__fields">
          <input
            v-model="inviteForm.email"
            type="email"
            class="usr-invite__email"
            placeholder="correo@empresa.com"
          />
          <select v-model="inviteForm.role" class="usr-invite__role">
            <option value="ADMIN">Administrador general</option>
            <option value="OPERATIONS">Operativo</option>
            <option value="FINANCE">Financiero</option>
            <option value="MAINTENANCE_SUPERVISOR">Supervisor de mantenimiento</option>
            <option value="TECHNICIAN">Técnico</option>
            <option value="CUSTOM">{{ customRole?.name || 'Personalizado' }}</option>
          </select>
          <select v-if="inviteForm.role === 'TECHNICIAN'" v-model="inviteForm.technicianId" class="usr-invite__role">
            <option value="" disabled>Vincular con técnico registrado...</option>
            <option v-for="t in technicians" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
          <input v-model="inviteForm.name" type="text" class="usr-invite__name" placeholder="Nombre (opcional)" />
        </div>
        <button class="usr-invite__btn" :disabled="!canInvite" @click="onInvite">
          Enviar invitación
        </button>
      </div>
      <div v-if="inviteError" class="usr-invite__error">{{ inviteError }}</div>
    </section>

    <!-- Zona 3 — Roles y permisos -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Roles y permisos</span>
      </div>
      <div class="usr-group__hint">Define qué puede ver y hacer cada rol en el sistema.</div>

      <div class="usr-role-cards">
        <div v-for="card in predefinedRoleCards" :key="card.role" class="usr-role-card">
          <div class="usr-role-card__header" :style="{ color: card.color }">
            {{ card.title }}
            <span class="usr-role-card__tag" :style="{ backgroundColor: card.bg, color: card.color }">
              {{ card.tag }}
            </span>
          </div>
          <ul class="usr-role-card__list">
            <li v-for="p in pages" :key="p.key" class="usr-role-card__row">
              <span
                class="mdi"
                :class="card.access.includes(p.key) ? 'mdi-check usr-check-ok' : 'mdi-close usr-check-no'"
              ></span>
              {{ p.label }}
            </li>
          </ul>
          <div class="usr-role-card__note">{{ card.note }}</div>
        </div>
      </div>

      <div class="usr-custom-role">
        <div class="usr-custom-role__header">
          <span class="usr-custom-role__label">Rol personalizado</span>
          <label class="usr-toggle">
            <input type="checkbox" v-model="customRoleEnabled" />
            <span class="usr-toggle__slider"></span>
          </label>
        </div>

        <div v-if="customRoleEnabled" class="usr-custom-role__builder">
          <table class="usr-perm-table">
            <thead>
              <tr>
                <th>Página</th>
                <th>Ver</th>
                <th>Editar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in customRolePages" :key="p.key">
                <td>{{ p.label }}</td>
                <td>
                  <label class="usr-toggle usr-toggle--sm">
                    <input
                      type="checkbox"
                      :checked="customPermissions[p.key].view"
                      @change="onToggleView(p.key, $event.target.checked)"
                    />
                    <span class="usr-toggle__slider"></span>
                  </label>
                </td>
                <td>
                  <label class="usr-toggle usr-toggle--sm">
                    <input
                      type="checkbox"
                      :checked="customPermissions[p.key].edit"
                      @change="onToggleEdit(p.key, $event.target.checked)"
                    />
                    <span class="usr-toggle__slider"></span>
                  </label>
                </td>
              </tr>
            </tbody>
          </table>

          <input
            v-model="customRoleName"
            type="text"
            class="usr-custom-role__name"
            placeholder="Nombre del rol personalizado"
          />
        </div>
      </div>
    </section>

    <button class="usr-save-btn" :disabled="!hasCustomRoleChanges" @click="onSaveCustomRole">
      Guardar configuración de usuarios
    </button>

    <!-- Zona 4 — Acceso de técnicos -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Acceso de técnicos</span>
      </div>

      <div class="usr-access-toggle-row">
        <div class="usr-access-toggle-row__text">
          <span class="usr-access-toggle-row__title">Permitir que los técnicos accedan al sistema</span>
          <span class="usr-access-toggle-row__desc">
            Los técnicos podrán ver y actualizar sus tickets asignados desde la plataforma.
          </span>
        </div>
        <label class="usr-toggle">
          <input type="checkbox" v-model="accessForm.technicianPortalEnabled" />
          <span class="usr-toggle__slider"></span>
        </label>
      </div>

      <div v-if="accessForm.technicianPortalEnabled" class="usr-sub-toggles">
        <label class="usr-sub-toggle usr-sub-toggle--locked">
          <input type="checkbox" checked disabled />
          Ver solo tickets asignados a ellos
        </label>
        <label class="usr-sub-toggle">
          <input type="checkbox" v-model="accessForm.technicianCanViewCosts" />
          Ver el costo registrado de sus tickets
        </label>
        <label class="usr-sub-toggle">
          <input type="checkbox" v-model="accessForm.technicianCanViewOwnMetrics" />
          Ver métricas de su propio desempeño
        </label>
      </div>

      <div class="usr-access-note">
        El acceso individual de cada técnico se activa desde su perfil en la subsección Técnicos o desde el
        gestor de técnicos en Mantenimiento.
        <router-link :to="{ path: '/configuracion', query: { section: 'tecnicos' } }" class="usr-access-note__link">
          Ir a Técnicos
        </router-link>
      </div>
    </section>

    <!-- Zona 5 — Portal de inquilinos -->
    <section class="usr-group">
      <div class="usr-group__header">
        <span class="usr-group__label">Portal de inquilinos</span>
      </div>

      <div class="usr-access-toggle-row">
        <div class="usr-access-toggle-row__text">
          <span class="usr-access-toggle-row__title">Activar portal para inquilinos</span>
          <span class="usr-access-toggle-row__desc">
            Los inquilinos podrán acceder a su contrato, estados de cuenta, y tickets desde un portal dedicado.
          </span>
        </div>
        <label class="usr-toggle">
          <input type="checkbox" v-model="accessForm.tenantPortalEnabled" />
          <span class="usr-toggle__slider"></span>
        </label>
      </div>

      <div v-if="accessForm.tenantPortalEnabled" class="usr-portal-config">
        <div class="usr-portal-group">
          <span class="usr-portal-group__title">Información financiera</span>
          <div class="usr-sub-toggles">
            <label class="usr-sub-toggle">
              <input type="checkbox" v-model="accessForm.tenantPortalShowStatements" />
              Ver sus estados de cuenta
            </label>
            <label class="usr-sub-toggle">
              <input type="checkbox" v-model="accessForm.tenantPortalShowChargeDetail" />
              Ver el detalle de cargos por concepto
            </label>
            <label class="usr-sub-toggle">
              <input type="checkbox" v-model="accessForm.tenantPortalShowPaymentHistory" />
              Ver historial de pagos registrados
            </label>
          </div>
        </div>

        <div class="usr-portal-group">
          <span class="usr-portal-group__title">Operación</span>
          <div class="usr-sub-toggles">
            <label class="usr-sub-toggle">
              <input type="checkbox" v-model="accessForm.tenantPortalAllowMaintenanceRequests" />
              Reportar solicitudes de mantenimiento
            </label>
            <label class="usr-sub-toggle">
              <input type="checkbox" v-model="accessForm.tenantPortalShowTicketStatus" />
              Ver el estatus de sus tickets activos
            </label>
          </div>
        </div>

        <div class="usr-field">
          <span class="usr-field__label">Información de contrato visible</span>
          <select v-model="accessForm.tenantPortalContractVisibility" class="usr-invite__role usr-field__select">
            <option value="ACTIVE_ONLY">Solo contrato activo actual</option>
            <option value="ACTIVE_PLUS_12M">Contrato activo y últimos 12 meses de historial</option>
            <option value="FULL_HISTORY">Historial completo</option>
          </select>
        </div>

        <div class="usr-field">
          <span class="usr-field__label">Mensaje de bienvenida</span>
          <textarea
            v-model="accessForm.tenantPortalWelcomeMessage"
            class="usr-welcome-textarea"
            rows="3"
            :placeholder="welcomePlaceholder"
          ></textarea>
        </div>

        <ParkContactsEditor :park-id="parkId" />
      </div>

      <div class="usr-access-note">
        El acceso individual de cada inquilino se activa desde el panel lateral de su contrato en la página
        de Contratos.
        <router-link to="/contratos" class="usr-access-note__link">Ir a Contratos</router-link>
      </div>
    </section>

    <button class="usr-save-btn" :disabled="!accessDirty" @click="onSaveAccessSettings">
      Guardar configuración de accesos externos
    </button>
    <div v-if="accessSaveError" class="usr-invite__error">{{ accessSaveError }}</div>

    <div v-if="confirmDeleteUserId" class="usr-confirm-overlay" @click="confirmDeleteUserId = null">
      <div class="usr-confirm-box" @click.stop>
        <p>¿Eliminar a este usuario? Esta acción no se puede deshacer.</p>
        <div class="usr-confirm-actions">
          <button @click="confirmDeleteUserId = null">Cancelar</button>
          <button class="usr-confirm-actions__danger" @click="onDelete(confirmDeleteUserId)">Eliminar</button>
        </div>
      </div>
    </div>
    <div v-if="showSavedToast" class="usr-toast">
      <span class="mdi mdi-check-circle"></span> Configuración de usuarios actualizada
    </div>
    <div v-if="accessSavedToast" class="usr-toast">
      <span class="mdi mdi-check-circle"></span> Configuración de accesos actualizada
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  getUsers,
  inviteUser,
  resendInvitation,
  changeUserRole,
  linkUserTechnician,
  suspendUser,
  reactivateUser,
  deleteUser,
  getCustomRole,
  saveCustomRole,
  getExternalAccesses,
  getAccessSettings,
  saveAccessSettings,
} from '@/services/users';
import { useAuthStore } from '@/services/authStore';
import { updateTechnician, getTechnicians } from '@/services/maintenance';
import { updatePortalAccess } from '@/services/contracts';
import ParkContactsEditor from '@/components/Configuracion/ParkContactsEditor.vue';

const props = defineProps({
  parkId: { type: String, required: true },
  parkName: { type: String, default: '' },
});
const emit = defineEmits(['dirty-change']);

const pages = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'finanzas', label: 'Finanzas' },
  { key: 'mantenimiento', label: 'Mantenimiento' },
  { key: 'servicios', label: 'Servicios' },
  { key: 'cobranza', label: 'Cobranza' },
  { key: 'configuracion', label: 'Configuración' },
];

// El constructor de rol personalizado incluye además las páginas de los
// portales externos, como placeholder, para cuando esos portales existan.
const customRolePages = [
  ...pages,
  { key: 'portalTecnicos', label: 'Portal de técnicos (próximamente)' },
  { key: 'portalInquilinos', label: 'Portal de inquilinos (próximamente)' },
];

const roleOptions = [
  { value: 'ADMIN', label: 'Administrador general' },
  { value: 'OPERATIONS', label: 'Operativo' },
  { value: 'FINANCE', label: 'Financiero' },
  { value: 'MAINTENANCE_SUPERVISOR', label: 'Supervisor de mantenimiento' },
  { value: 'TECHNICIAN', label: 'Técnico' },
  { value: 'CUSTOM', label: 'Personalizado' },
];

const predefinedRoleCards = [
  {
    role: 'ADMIN',
    title: 'Administrador general',
    tag: 'Acceso completo',
    color: '#3b82f6',
    bg: '#dbeafe',
    access: pages.map((p) => p.key),
    note: 'Incluye acceso a Configuración y gestión de usuarios.',
  },
  {
    role: 'OPERATIONS',
    title: 'Operativo',
    tag: 'Acceso operativo',
    color: '#10b981',
    bg: '#d1fae5',
    access: ['dashboard', 'contratos', 'mantenimiento', 'servicios'],
    note: 'Sin acceso a información financiera ni configuración del sistema.',
  },
  {
    role: 'FINANCE',
    title: 'Financiero',
    tag: 'Acceso financiero',
    color: '#8b5cf6',
    bg: '#ede9fe',
    access: ['dashboard', 'finanzas', 'cobranza'],
    note: 'Solo acceso a información financiera y de cobranza.',
  },
  {
    role: 'MAINTENANCE_SUPERVISOR',
    title: 'Supervisor de mantenimiento',
    tag: 'Acceso de mantenimiento',
    color: '#f0a500',
    bg: '#fef3c7',
    access: ['dashboard', 'mantenimiento'],
    note: 'Sin acceso a Contratos, Finanzas, Cobranza, Servicios ni Configuración.',
  },
  {
    role: 'TECHNICIAN',
    title: 'Técnico',
    tag: 'Solo su portal',
    color: '#0ea5e9',
    bg: '#e0f2fe',
    access: [],
    note: 'Accede a su portal de tickets y calendario preventivo (si el portal de técnicos está activado) y a su cuenta.',
  },
];

const { state: authState } = useAuthStore();
// Quién soy, según la sesión real (ya no el usuario "actual" simulado de antes).
const currentUserId = computed(() => authState.user?.id || null);

const users = ref([]);
const inviteForm = reactive({ email: '', role: 'OPERATIONS', name: '', technicianId: '' });
const technicians = ref([]);
const canInvite = computed(
  () => !!inviteForm.email && (inviteForm.role !== 'TECHNICIAN' || !!inviteForm.technicianId)
);
const inviteError = ref('');

const externalAccesses = ref([]);
const togglingId = ref(null);

const openMenuUserId = ref(null);
const roleMenuUserId = ref(null);
const techMenuUserId = ref(null);
const linkError = ref('');
const menuPosition = reactive({ top: '0px', left: '0px' });
const confirmDeleteUserId = ref(null);
const showSavedToast = ref(false);

const customRole = ref(null);
const customRoleEnabled = ref(false);
const customRoleName = ref('');
const customPermissions = reactive(
  Object.fromEntries(customRolePages.map((p) => [p.key, { view: false, edit: false }]))
);
const originalCustomRoleSnapshot = ref(null);

const activeMenuUser = computed(() => users.value.find((u) => u.id === openMenuUserId.value) || null);

// --- Configuración de accesos externos (técnicos / portal de inquilinos) ---
const accessForm = reactive({
  technicianPortalEnabled: false,
  technicianCanViewCosts: false,
  technicianCanViewOwnMetrics: true,
  tenantPortalEnabled: false,
  tenantPortalShowStatements: true,
  tenantPortalShowChargeDetail: true,
  tenantPortalShowPaymentHistory: true,
  tenantPortalAllowMaintenanceRequests: true,
  tenantPortalShowTicketStatus: true,
  tenantPortalContractVisibility: 'ACTIVE_PLUS_12M',
  tenantPortalWelcomeMessage: '',
});
const accessDirty = ref(false);
const accessSavedToast = ref(false);
const accessSaveError = ref('');
let accessLoaded = false;

const welcomePlaceholder = computed(
  () =>
    `Bienvenido al portal de ${props.parkName || '[nombre del parque]'}. Aquí podrás consultar tu contrato, revisar tus estados de cuenta, y reportar solicitudes de mantenimiento.`
);

async function loadAccessSettings() {
  const settings = await getAccessSettings(props.parkId);
  if (settings) {
    Object.assign(accessForm, settings, {
      tenantPortalWelcomeMessage: settings.tenantPortalWelcomeMessage || '',
    });
  }
  accessLoaded = true;
}

watch(accessForm, () => {
  if (!accessLoaded) return;
  accessDirty.value = true;
});

async function onSaveAccessSettings() {
  accessSaveError.value = '';
  try {
    await saveAccessSettings(props.parkId, {
      ...accessForm,
      tenantPortalWelcomeMessage: accessForm.tenantPortalWelcomeMessage || null,
    });
  } catch (err) {
    // Antes se mostraba "guardado" aunque el servidor rechazara el cambio.
    accessSaveError.value = 'No se pudo guardar la configuración. Intenta de nuevo.';
    return;
  }
  accessDirty.value = false;
  accessSavedToast.value = true;
  setTimeout(() => {
    accessSavedToast.value = false;
  }, 3000);
}

async function loadExternalAccesses() {
  externalAccesses.value = await getExternalAccesses(props.parkId);
}

async function onToggleExternalAccess(row) {
  togglingId.value = row.id;
  try {
    if (row.sourceType === 'TECHNICIAN') {
      await updateTechnician(row.sourceId, { platformAccessEnabled: false });
    } else {
      await updatePortalAccess(row.sourceId, false);
    }
    await loadExternalAccesses();
  } finally {
    togglingId.value = null;
  }
}

function initials(u) {
  const source = u.name || u.email;
  return source
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function roleLabel(u) {
  if (u.role === 'CUSTOM') return u.customRole?.name || customRole.value?.name || 'Personalizado';
  return roleOptions.find((r) => r.value === u.role)?.label || u.role;
}

function formatRelativeAccess(u) {
  if (u.status === 'PENDING' || !u.lastLoginAt) return 'Sin acceso aún';
  const date = new Date(u.lastLoginAt);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays === 0) {
    return `Hoy ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
  if (diffDays < 7) return `Hace ${diffDays} día${diffDays === 1 ? '' : 's'}`;
  const diffWeeks = Math.floor(diffDays / 7);
  return `Hace ${diffWeeks} semana${diffWeeks === 1 ? '' : 's'}`;
}

async function loadUsers() {
  users.value = await getUsers(props.parkId);
}

async function loadCustomRole() {
  const role = await getCustomRole(props.parkId);
  customRole.value = role;
  if (role) {
    customRoleEnabled.value = true;
    customRoleName.value = role.name;
    Object.entries(role.permissions).forEach(([key, val]) => {
      if (customPermissions[key]) {
        customPermissions[key].view = val.view;
        customPermissions[key].edit = val.edit;
      }
    });
  }
  originalCustomRoleSnapshot.value = JSON.stringify({
    name: customRoleName.value,
    permissions: JSON.parse(JSON.stringify(customPermissions)),
  });
}

const hasCustomRoleChanges = computed(() => {
  if (!customRoleEnabled.value) return false;
  if (!customRoleName.value.trim()) return false;
  const current = JSON.stringify({ name: customRoleName.value, permissions: customPermissions });
  return current !== originalCustomRoleSnapshot.value;
});

watch(hasCustomRoleChanges, (isDirty) => emit('dirty-change', isDirty));

function onToggleView(key, checked) {
  customPermissions[key].view = checked;
  if (!checked) customPermissions[key].edit = false;
}

function onToggleEdit(key, checked) {
  customPermissions[key].edit = checked;
  if (checked) customPermissions[key].view = true;
}

async function onSaveCustomRole() {
  const saved = await saveCustomRole(props.parkId, {
    name: customRoleName.value,
    permissions: customPermissions,
  });
  customRole.value = saved;
  originalCustomRoleSnapshot.value = JSON.stringify({
    name: customRoleName.value,
    permissions: JSON.parse(JSON.stringify(customPermissions)),
  });
  showSavedToast.value = true;
  setTimeout(() => {
    showSavedToast.value = false;
  }, 3000);
}

async function onInvite() {
  inviteError.value = '';
  try {
    await inviteUser(props.parkId, {
      email: inviteForm.email,
      role: inviteForm.role,
      name: inviteForm.name || undefined,
      customRoleId: inviteForm.role === 'CUSTOM' ? customRole.value?.id : undefined,
      technicianId: inviteForm.role === 'TECHNICIAN' ? inviteForm.technicianId : undefined,
    });
    inviteForm.email = '';
    inviteForm.name = '';
    inviteForm.technicianId = '';
    await loadUsers();
  } catch (error) {
    if (error.data?.error === 'EMAIL_ALREADY_HAS_ACCESS') {
      const role = roleOptions.find((r) => r.value === error.data.currentRole)?.label || '';
      inviteError.value = `Este correo ya tiene acceso al sistema · Rol actual: ${role}`;
    } else if (error.data?.error === 'PLAN_LIMIT_USERS') {
      inviteError.value = error.data.message;
    } else {
      inviteError.value = 'No se pudo enviar la invitación.';
    }
  }
}

function toggleMenu(userId, event) {
  if (openMenuUserId.value === userId) {
    openMenuUserId.value = null;
    roleMenuUserId.value = null;
    techMenuUserId.value = null;
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  menuPosition.top = `${rect.bottom + 4}px`;
  menuPosition.left = `${rect.right - 180}px`;
  openMenuUserId.value = userId;
  roleMenuUserId.value = null;
  techMenuUserId.value = null;
}

function closeMenu() {
  openMenuUserId.value = null;
  roleMenuUserId.value = null;
  techMenuUserId.value = null;
}

async function onChangeRole(userId, role) {
  await changeUserRole(userId, role, role === 'CUSTOM' ? customRole.value?.id : undefined);
  await loadUsers();
  // Un usuario que pasa a Técnico necesita un técnico del catálogo vinculado: se pide enseguida.
  if (role === 'TECHNICIAN') {
    roleMenuUserId.value = null;
    techMenuUserId.value = userId;
    return;
  }
  closeMenu();
}

// Un técnico del catálogo solo puede estar ligado a una cuenta.
function isTechnicianTaken(technicianId, exceptUserId) {
  return users.value.some((u) => u.technicianId === technicianId && u.id !== exceptUserId);
}

async function onLinkTechnician(userId, technicianId) {
  try {
    await linkUserTechnician(userId, technicianId);
    await loadUsers();
    closeMenu();
  } catch (err) {
    linkError.value = err.message === 'TECHNICIAN_ALREADY_LINKED'
      ? 'Ese técnico ya está vinculado a otra cuenta.'
      : 'No se pudo vincular el técnico.';
    setTimeout(() => { linkError.value = ''; }, 4000);
    closeMenu();
  }
}

async function onResendInvite(userId) {
  await resendInvitation(userId);
  closeMenu();
}

async function onSuspend(userId) {
  await suspendUser(userId);
  await loadUsers();
  closeMenu();
}

async function onReactivate(userId) {
  try {
    await reactivateUser(userId);
    await loadUsers();
  } catch (err) {
    // Reactivar vuelve a ocupar un lugar del plan: puede rebasar el límite de usuarios.
    linkError.value = err.data?.error === 'PLAN_LIMIT_USERS' ? err.data.message : 'No se pudo reactivar el acceso.';
    setTimeout(() => { linkError.value = ''; }, 5000);
  }
  closeMenu();
}

async function onDelete(userId) {
  await deleteUser(userId);
  confirmDeleteUserId.value = null;
  await loadUsers();
}

async function loadTechnicians() {
  try {
    technicians.value = await getTechnicians(props.parkId);
  } catch {
    technicians.value = [];
  }
}

onMounted(() => {
  loadTechnicians();
  loadUsers();
  loadCustomRole();
  loadExternalAccesses();
  loadAccessSettings();
  document.addEventListener('click', closeMenu);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', closeMenu);
});
</script>

<style scoped>
.usr-group { margin-bottom: 32px; }
.usr-group__header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.usr-group__label { font: 500 12px Inter, sans-serif; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; }
.usr-group__count { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.usr-group__hint { font: 400 11px Inter, sans-serif; color: #94a3b8; margin-bottom: 12px; }

.usr-divider { border: none; border-top: 1px solid #f1f5f9; margin: 0 0 24px; }

.usr-table { width: 100%; border-collapse: collapse; }
.usr-table__row { border-bottom: 1px solid #f1f5f9; }
.usr-table__col { padding: 10px 8px; vertical-align: middle; }
.usr-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font: 600 11px Inter, sans-serif; }
.usr-table__name-line { font: 500 13px Inter, sans-serif; color: #0f172a; display: flex; align-items: center; gap: 6px; }
.usr-table__email-line { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.usr-tag-you { font: 500 10px Inter, sans-serif; color: #3b82f6; background: #dbeafe; padding: 1px 6px; border-radius: 8px; }

.usr-role-pill { font: 500 11px Inter, sans-serif; padding: 3px 10px; border-radius: 12px; color: #fff; white-space: nowrap; }
.usr-role-pill--admin { background: #3b82f6; }
.usr-role-pill--operations { background: #10b981; }
.usr-role-pill--finance { background: #8b5cf6; }
.usr-role-pill--maintenance_supervisor { background: #f0a500; }
.usr-role-pill--custom { background: #64748b; }
.usr-role-pill--technician { background: #0ea5e9; }
.usr-role-pill--tenant { background: #14b8a6; }

.usr-access-text { font: 400 12px Inter, sans-serif; color: #64748b; }
.usr-access--pending { color: #d97706; }

.usr-actions-btn { border: none; background: none; cursor: pointer; color: #94a3b8; }
.usr-actions-btn:hover { color: #334155; }

.usr-menu { position: fixed; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); min-width: 180px; z-index: 1000; padding: 4px; }
.usr-menu__item { display: block; width: 100%; text-align: left; background: none; border: none; padding: 8px 10px; font: 400 12px Inter, sans-serif; color: #334155; cursor: pointer; border-radius: 4px; }
.usr-menu__item:hover { background: #f8fafc; }
.usr-menu__item--danger { color: #dc2626; }
.usr-menu__role-picker { display: flex; flex-direction: column; }
.usr-menu__role-opt { display: block; width: 100%; text-align: left; background: none; border: none; padding: 8px 10px; font: 400 12px Inter, sans-serif; color: #334155; cursor: pointer; border-radius: 4px; }
.usr-menu__role-opt:hover { background: #f8fafc; }
.usr-menu__role-opt:disabled { color: #cbd5e1; cursor: not-allowed; background: none; }
.usr-menu__role-opt--danger { color: #dc2626; }
.usr-menu__taken { font-size: 10px; }
.usr-menu__empty { padding: 8px 10px; font: 400 12px Inter, sans-serif; color: #94a3b8; }
.usr-tech-link { display: flex; align-items: center; gap: 4px; margin-top: 4px; font: 400 11px Inter, sans-serif; color: #64748b; }
.usr-tech-link--missing { color: #b45309; }
.usr-link-error { margin: 8px 0; padding: 8px 12px; border-radius: 6px; background: #fef2f2; color: #b91c1c; font: 400 12px Inter, sans-serif; }

.usr-invite-note { background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; font: 400 11px Inter, sans-serif; color: #64748b; margin-bottom: 10px; }

.usr-invite { display: flex; flex-direction: column; gap: 10px; }
.usr-invite__fields { display: flex; gap: 8px; flex-wrap: wrap; }
.usr-invite__email { flex: 1 1 220px; min-width: 0; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #1e293b; color-scheme: light; font: 400 13px Inter, sans-serif; }
.usr-invite__role { flex: 1 1 200px; min-width: 0; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #1e293b; color-scheme: light; font: 400 13px Inter, sans-serif; }
.usr-invite__name { flex: 1 1 160px; min-width: 0; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #1e293b; color-scheme: light; font: 400 13px Inter, sans-serif; }
.usr-invite__email:focus, .usr-invite__role:focus, .usr-invite__name:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15); }
.usr-invite__email::placeholder, .usr-invite__name::placeholder { color: #94a3b8; }
.usr-invite__btn { align-self: flex-start; padding: 8px 14px; background: #3b82f6; color: #fff; border: none; border-radius: 6px; font: 500 13px Inter, sans-serif; cursor: pointer; }
.usr-invite__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.usr-invite__error { margin-top: 6px; font: 400 11px Inter, sans-serif; color: #dc2626; }

.usr-role-cards { display: flex; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
.usr-role-card { flex: 1; min-width: 200px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.usr-role-card__header { font: 500 13px Inter, sans-serif; display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.usr-role-card__tag { font: 400 10px Inter, sans-serif; padding: 2px 8px; border-radius: 10px; }
.usr-role-card__list { list-style: none; padding: 0; margin: 0 0 10px; }
.usr-role-card__row { display: flex; align-items: center; gap: 8px; font: 400 12px Inter, sans-serif; color: #334155; padding: 3px 0; }
.usr-check-ok { color: #10b981; }
.usr-check-no { color: #ef4444; }
.usr-role-card__note { font: 400 10px Inter, sans-serif; color: #94a3b8; }

.usr-custom-role { margin-top: 16px; border-top: 1px solid #f1f5f9; padding-top: 16px; }
.usr-custom-role__header { display: flex; align-items: center; gap: 10px; }
.usr-custom-role__label { font: 500 12px Inter, sans-serif; color: #334155; }
.usr-custom-role__builder { margin-top: 12px; }
.usr-perm-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
.usr-perm-table th { text-align: left; font: 500 11px Inter, sans-serif; color: #94a3b8; padding: 6px 8px; }
.usr-perm-table td { padding: 6px 8px; font: 400 12px Inter, sans-serif; color: #334155; }
.usr-custom-role__name { width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 13px Inter, sans-serif; }

.usr-toggle { position: relative; display: inline-block; width: 34px; height: 18px; flex-shrink: 0; }
.usr-toggle input { opacity: 0; width: 0; height: 0; }
.usr-toggle__slider { position: absolute; inset: 0; background: #cbd5e1; border-radius: 20px; transition: 0.15s; }
.usr-toggle__slider::before { content: ''; position: absolute; height: 14px; width: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; transition: 0.15s; }
.usr-toggle input:checked + .usr-toggle__slider { background: #3b82f6; }
.usr-toggle input:checked + .usr-toggle__slider::before { transform: translateX(16px); }
.usr-toggle input:disabled + .usr-toggle__slider { opacity: 0.6; cursor: not-allowed; }

.usr-save-btn { padding: 10px 18px; background: #3b82f6; color: #fff; border: none; border-radius: 6px; font: 500 13px Inter, sans-serif; cursor: pointer; margin-bottom: 32px; }
.usr-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.usr-empty { font: 400 12px Inter, sans-serif; color: #94a3b8; padding: 16px 0; }

.usr-access-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 0; }
.usr-access-toggle-row__text { display: flex; flex-direction: column; gap: 2px; }
.usr-access-toggle-row__title { font: 500 12px Inter, sans-serif; color: #334155; text-transform: uppercase; letter-spacing: 0.3px; }
.usr-access-toggle-row__desc { font: 400 11px Inter, sans-serif; color: #94a3b8; }

.usr-sub-toggles { display: flex; flex-direction: column; gap: 10px; padding: 4px 0 12px 0; }
.usr-sub-toggle { display: flex; align-items: center; gap: 8px; font: 400 12px Inter, sans-serif; color: #334155; }
.usr-sub-toggle input[type="checkbox"] { width: 14px; height: 14px; }
.usr-sub-toggle--locked { opacity: 0.7; }

.usr-portal-config { display: flex; flex-direction: column; gap: 4px; padding-bottom: 8px; }
.usr-portal-group { padding: 6px 0; }
.usr-portal-group__title { display: block; font: 500 11px Inter, sans-serif; text-transform: uppercase; letter-spacing: 0.4px; color: #94a3b8; margin-bottom: 6px; }

.usr-field { display: flex; flex-direction: column; gap: 6px; padding: 10px 0; }
.usr-field__label { font: 500 11px Inter, sans-serif; color: #64748b; }
.usr-field__select { width: 100%; flex: none; }
.usr-welcome-textarea { width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 12px Inter, sans-serif; color: #334155; resize: vertical; }

.usr-access-note { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 6px; padding: 8px 12px; font: 400 11px Inter, sans-serif; color: #3b82f6; margin-top: 8px; }
.usr-access-note__link { color: #1d4ed8; font-weight: 500; text-decoration: underline; margin-left: 4px; }

.usr-confirm-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.usr-confirm-box { background: #fff; border-radius: 8px; padding: 20px; width: 320px; }
.usr-confirm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.usr-confirm-actions__danger { background: #dc2626; color: #fff; border: none; padding: 6px 12px; border-radius: 6px; }
.usr-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #10b981;
  color: #fff;
  padding: 10px 16px;
  border-radius: 6px;
  font: 500 13px Inter, sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 3000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
</style>